// Base
import { BaseApi } from 'api/base.api'

// Interface
import {
  IUserApi,
  IUserApiRequest,
  IUserApiGetListAttrs,
  IUserApiRejectAttrs,
  IUserApiApproveAttrs,
  IUserApiDeleteAttrs,
  IUserApiUpdateAttrs,
  IUserApiCreateAttrs,
  IUserApiValidateEmailResponse,
  IUserApiValidateEmailAttrs,
  IUserApiGetDropdownAttrs,
  IUserApiDownloadAttrs,
  IUserApiDownloadResponse,
  IUserApiListResponse,
  IUserApiDetailResponse,
  IUserApiGetDetailAttrs,
  IUserApiNullResponse,
  IUserApiDropdownResponse,
} from 'interfaces/back-office/user/user-api.interface'
import { IUserUpdateForm } from 'interfaces/back-office/user'

// Constant
import { ApiConstant } from 'constant'

// Utils
import { axios_renderUrl } from 'utils'

export class UserApi extends BaseApi<IUserApi> implements IUserApiRequest {
  readonly url = ApiConstant.USER_URL
  private userUrl = axios_renderUrl(this.url, ApiConstant.BACK_OFFICE)

  /**
   * @description Get user list
   *
   * @method POST
   * @access private (only user that logged in)
   *
   * @param {IUserApiGetListAttrs} payload
   *
   * @return {Promise<IUserApiListResponse>} Promise<IUserApiListResponse>
   */
  async user_getList(payload: IUserApiGetListAttrs): Promise<IUserApiListResponse> {
    return this.POST(`${this.userUrl}/list`, payload.body, { ...payload.requestConfig })
  }

  /**
   * @description Get single user
   *
   * @method POST
   * @access private (only user that logged in)
   *
   * @param {IUserApiGetDetailAttrs} payload
   *
   * @return {Promise<IUserApiDetailResponse>} Promise<IUserApiDetailResponse>
   */
  async user_getDetail(payload: IUserApiGetDetailAttrs): Promise<IUserApiDetailResponse> {
    return this.POST(`${this.userUrl}/${payload.params.id}`, {}, { ...payload.requestConfig })
  }

  /**
   * @description Bulk create user
   *
   * @method POST
   * @access private (only user that logged in)
   *
   * @param {IUserApiCreateAttrs} payload
   *
   * @return {Promise<IUserApiNullResponse>} Promise<IUserApiNullResponse>
   */
  async user_create(payload: IUserApiCreateAttrs): Promise<IUserApiNullResponse> {
    return this.POST(
      `${this.userUrl}/create`,
      {
        users: payload.body.users.map((user: IUserUpdateForm) => {
          return {
            ...user,
            roleIds: user.roleIds?.toString() || '',
          }
        }),
      },
      { ...payload.requestConfig },
    )
  }

  /**
   * @description Approve user
   *
   * @method POST
   * @access private (only user that logged in)
   *
   * @param {IUserApiApproveAttrs} payload
   *
   * @return {Promise<IUserApiNullResponse>} Promise<IUserApiNullResponse>
   */
  async user_approve(payload: IUserApiApproveAttrs): Promise<IUserApiNullResponse> {
    return this.POST(
      `${this.userUrl}/approve/${payload.params.id}`,
      {
        ...payload.body,
        roleIds: payload.body.roleIds?.toString() || '',
      },
      { ...payload.requestConfig },
    )
  }

  /**
   * @description Reject user
   *
   * @method POST
   * @access private (only user that logged in)
   *
   * @param {IUserApiRejectAttrs} payload
   *
   * @return {Promise<IUserApiNullResponse>} Promise<IUserApiNullResponse>
   */
  async user_reject(payload: IUserApiRejectAttrs): Promise<IUserApiNullResponse> {
    return this.POST(`${this.userUrl}/reject/${payload.params.id}`, payload.body, { ...payload.requestConfig })
  }

  /**
   * @description Delete user
   *
   * @method POST
   * @access private (only user that logged in)
   *
   * @param {IUserApiDeleteAttrs} payload
   *
   * @return {Promise<IUserApiNullResponse>} Promise<IUserApiNullResponse>
   */
  async user_delete(payload: IUserApiDeleteAttrs): Promise<IUserApiNullResponse> {
    return this.POST(`${this.userUrl}/delete/${payload.params.id}`, { ...payload.requestConfig })
  }

  /**
   * @description Update user
   *
   * @method POST
   * @access private (only user that logged in)
   *
   * @param {IUserApiUpdateAttrs} payload
   *
   * @return {Promise<IUserApiNullResponse>} Promise<IUserApiNullResponse>
   */
  async user_update(payload: IUserApiUpdateAttrs): Promise<IUserApiNullResponse> {
    return this.POST(
      `${this.userUrl}/update/${payload.params.id}`,
      { ...payload.body, roleIds: payload.body.roleIds?.toString() || '' },
      { ...payload.requestConfig },
    )
  }

  /**
   * @description Validate email of user
   *
   * @method POST
   * @access private (only user that logged in)
   *
   * @param {IUserApiValidateEmailAttrs} payload
   *
   * @return {Promise<IUserApiValidateEmailResponse>} Promise<IUserApiValidateEmailResponse>
   */
  async user_validateEmail(payload: IUserApiValidateEmailAttrs): Promise<IUserApiValidateEmailResponse> {
    return this.POST(`${this.userUrl}/email/${payload.params.email}`, payload.body, {
      ...payload.requestConfig,
    })
  }

  /**
   * @description Dropdown of users
   *
   * @method POST
   * @access private (only user that logged in)
   *
   * @param {IUserApiGetDropdownAttrs} payload
   *
   * @return {Promise<IUserApiDropdownResponse>} Promise<IUserApiDropdownResponse>
   */
  async user_getDropdown(payload: IUserApiGetDropdownAttrs): Promise<IUserApiDropdownResponse> {
    return await this.POST(
      `${this.userUrl}/dropdown`,
      {},
      {
        ...payload.requestConfig,
      },
    )
  }

  /**
   * @description Download
   *
   * @method POST
   * @access private (only user that logged in)
   *
   * @param {IUserApiDownloadAttrs} payload
   *
   * @return {Promise<IUserApiDownloadResponse>} Promise<IUserApiDownloadResponse>
   */
  async user_download(payload: IUserApiDownloadAttrs): Promise<IUserApiDownloadResponse> {
    return this.POST(
      `${this.userUrl}/download/${payload.params.blobId}`,
      {},
      {
        ...payload.requestConfig,
        responseType: 'blob',
      },
    )
  }
}
